/**
 * markdownParser — prosemirror-markdown based Markdown ↔ HTML conversion.
 *
 * Uses `markdown-it` (AST-based) instead of custom regex for proper parsing.
 * Supports: GFM task lists, highlight (::text::), strikethrough, tables, etc.
 *
 * Two main exports:
 * - `markdownToHtml(md)` — Markdown string → HTML string (for server, migration)
 * - `markdownToHtmlAdvanced(md)` — Same but with task list DOM fixup for TipTap
 *
 * @see docHelpers.ts — calls markdownToHtml for the markdown→Y.Doc pipeline
 * @see EditorShell.tsx — uses clipboardTextParser for paste handling (separate)
 */

import MarkdownIt from "markdown-it"
// @ts-expect-error — no type declarations for markdown-it-task-lists
import markdownItTaskLists from "markdown-it-task-lists"

// ---------------------------------------------------------------------------
// markdown-it instance with plugins
// ---------------------------------------------------------------------------

/**
 * Custom markdown-it plugin for `::highlighted text::` syntax.
 * Mirrors v1's markdownItHighlight implementation.
 */
function markdownItHighlight(md: MarkdownIt): void {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function tokenize(state: any, silent: boolean): boolean {
    const marker = "::"
    const start = state.pos

    if (state.src.slice(start, start + 2) !== marker) return false

    const end = state.src.indexOf(marker, start + 2)
    if (end === -1) return false

    if (!silent) {
      let token = state.push("mark_open", "mark", 1)
      token.markup = marker

      token = state.push("text", "", 0)
      token.content = state.src.slice(start + 2, end)

      token = state.push("mark_close", "mark", -1)
      token.markup = marker
    }
    state.pos = end + 2
    return true
  }

  md.inline.ruler.before("emphasis", "highlight", tokenize)
}

/**
 * Configured markdown-it instance.
 * - linkify: auto-detect URLs
 * - task lists: `- [ ]` / `- [x]`
 * - highlight: `::text::`
 */
export const mdParser = new MarkdownIt({ linkify: true, html: false })
  .use(markdownItTaskLists, { enabled: true, label: false })
  .use(markdownItHighlight)

// ---------------------------------------------------------------------------
// Markdown → HTML (for server, migration, docHelpers)
// ---------------------------------------------------------------------------

/**
 * Convert Markdown to HTML using markdown-it (AST-based).
 *
 * Handles: headings, bold, italic, code, links, images, lists (nested),
 * task lists (- [ ]/- [x]), blockquotes, horizontal rules, strikethrough,
 * code blocks (fenced + indented), tables (GFM), highlight (::text::).
 */
export function markdownToHtmlMd(md: string): string {
  if (!md) return ""
  return mdParser.render(md)
}

/**
 * Detect whether a string likely contains Markdown formatting.
 * Used for heuristic paste detection — avoid converting plain text.
 */
export function looksLikeMarkdown(text: string): boolean {
  // Quick check for common markdown patterns
  return /^#{1,6}\s|\*\*.*\*\*|__.*__|^[-*+]\s|\n[-*+]\s|^>\s|\n>\s|^```|^\d+\.\s|\[.*\]\(.*\)|^[-*_]{3,}\s*$/m.test(
    text,
  )
}
